import { useEffect, useMemo, useState } from "react";
import { CalendarRange, ChevronLeft, LogOut, ShieldCheck } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { useAuth } from "../context/AuthContext";
import { CompanyMark, companyBrand } from "../branding/companyBrand";

const companyVisual = {
  sgp: {
    panel: "bg-slate-950 text-white border-slate-800",
    badge: "bg-white",
    accent: "text-amber-400",
    ring: "hover:border-amber-400/70",
  },
  prachi: {
    panel: "bg-[#172b3a] text-white border-[#354f63]",
    badge: "bg-[#eaf0f5]",
    accent: "text-sky-200",
    ring: "hover:border-sky-300",
  },
  neo: {
    panel: "bg-gradient-to-br from-emerald-950 via-teal-900 to-cyan-900 text-white border-cyan-700/50",
    badge: "bg-white",
    accent: "text-cyan-300",
    ring: "hover:border-cyan-300",
  },
};

export default function WorkspaceSelect() {
  const { user, workspace, setWorkspace, logout } = useAuth();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/workspaces")
      .then(({ data }) => {
        setCompanies(data.companies || []);
        if (workspace?.company?.id) {
          const current = (data.companies || []).find((c) => c.id === workspace.company.id);
          if (current) setSelected(current);
        }
      })
      .catch((err) => setError(err.message || "Could not load companies"))
      .finally(() => setBusy(false));
  }, [workspace?.company?.id]);

  const currentFY = useMemo(() => {
    if (!selected?.financial_years?.length) return "";
    return selected.financial_years[selected.financial_years.length - 1];
  }, [selected]);

  const chooseYear = (financialYear) => {
    setWorkspace({ company: selected, financial_year: financialYear });
    navigate("/", { replace: true });
  };

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <main className="min-h-screen bg-[#f4f6fb] px-6 py-8 lg:px-10">
      <div className="mx-auto max-w-6xl">
        <header className="mb-8 flex items-center justify-between gap-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-400">SGP Office OS</p>
            <h1 className="mt-1 text-3xl font-heading font-bold tracking-tight text-slate-950">
              {selected ? "Choose financial year" : "Choose company"}
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Signed in as {user?.name || user?.email}. Each company and year is a separate workspace.
            </p>
          </div>
          <button onClick={doLogout} className="sgp-secondary-btn">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </header>

        {error && <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">{error}</div>}
        {busy && <div className="sgp-card p-10 text-center text-sm text-slate-400">Loading workspaces…</div>}

        <AnimatePresence mode="wait">
          {!busy && !selected && (
            <motion.section
              key="companies"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className="grid gap-5 md:grid-cols-3"
            >
              {companies.map((company, index) => {
                const visual = companyVisual[company.id] || companyVisual.sgp;
                return (
                  <motion.button
                    key={company.id}
                    data-testid={`workspace-company-${company.id}`}
                    onClick={() => setSelected(company)}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.06 }}
                    className={`min-h-[250px] rounded-3xl border p-6 text-left shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl ${visual.panel} ${visual.ring}`}
                  >
                    <div className="mb-12">
                      <CompanyMark company={company} className="h-20 w-20" imageClassName="h-[4.5rem] w-[4.5rem]" />
                    </div>
                    <p className={`text-[10px] font-bold uppercase tracking-[0.18em] ${visual.accent}`}>
                      Company workspace
                    </p>
                    <h2 className="mt-2 text-2xl font-heading font-bold">{company.name}</h2>
                    <p className="mt-3 text-sm opacity-70">Sales · Purchase · Stock · GST · Tenders · Accounts · Documents</p>
                  </motion.button>
                );
              })}
            </motion.section>
          )}

          {!busy && selected && (
            <motion.section
              key="years"
              initial={{ opacity: 0, x: 16 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -12 }}
            >
              <button onClick={() => setSelected(null)} className="mb-5 inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-950">
                <ChevronLeft className="h-4 w-4" /> Back to companies
              </button>

              <div className={`mb-6 flex items-center gap-4 rounded-2xl border p-5 shadow-sm ${companyBrand(selected).shell === "neo" ? "border-cyan-200 bg-emerald-50/80" : companyBrand(selected).shell === "prachi" ? "border-slate-200 bg-slate-50" : "border-slate-200 bg-white"}`}>
                <CompanyMark company={selected} className="h-14 w-14" imageClassName="h-12 w-12" />
                <div>
                  <p className="font-heading text-xl font-bold text-slate-950">{selected.name}</p>
                  <p className="text-xs text-slate-500">Financial years available from {selected.start_year}</p>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                {[...(selected.financial_years || [])].reverse().map((fy, index) => {
                  const current = fy === currentFY;
                  return (
                    <motion.button
                      key={fy}
                      data-testid={`workspace-year-${fy}`}
                      onClick={() => chooseYear(fy)}
                      initial={{ opacity: 0, scale: 0.98 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: Math.min(index, 8) * 0.025 }}
                      className={`group rounded-2xl border bg-white p-5 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-slate-400 hover:shadow-md ${
                        current ? "ring-1 ring-slate-900/10" : "border-slate-200"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <CalendarRange className="h-5 w-5 text-slate-500" />
                        {current && (
                          <span className="rounded-full bg-emerald-50 px-2 py-1 text-[9px] font-bold uppercase tracking-wider text-emerald-700">
                            Current
                          </span>
                        )}
                      </div>
                      <p className="mt-7 font-mono text-xl font-bold text-slate-900">{fy}</p>
                      <p className="mt-1 text-xs text-slate-500">{current ? "Live working year" : "Historical workspace"}</p>
                    </motion.button>
                  );
                })}
              </div>

              <div className="mt-7 flex items-center gap-2 text-xs text-slate-500">
                <ShieldCheck className="h-4 w-4 text-emerald-600" />
                Company and financial-year data are isolated on the server, not only hidden in the UI.
              </div>
            </motion.section>
          )}
        </AnimatePresence>
      </div>
    </main>
  );
}
